// A trailing `Z` or an explicit `±HH:MM` / `±HHMM` offset — the only cases
// where the instant is unambiguous and safe to re-express in Eastern time.
// Without one, `new Date(raw)` would anchor the value to the analyst's local
// zone (see `formatNoteDatetime`).
const HAS_TIMEZONE = /(?:Z|[+-]\d{2}:?\d{2})$/;

// Fixed to America/New_York so every analyst sees the same wall-clock time
// regardless of their machine's locale. Mirrors the sibling Pre-approval Change
// History formatter (features/audit/formatChangeDate), but drops seconds and
// uses a numeric hour to match the Analyst Notes design (MM/DD/YYYY h:mm AM/PM).
const EST_FORMAT = new Intl.DateTimeFormat("en-US", {
  timeZone: "America/New_York",
  month: "2-digit",
  day: "2-digit",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  hour12: true,
});

/**
 * Formats an analyst-note timestamp as `MM/DD/YYYY h:mm AM/PM` in Eastern time.
 * recap's exact wire format is not pinned by the BFF contract (the DTO types
 * `createdDatetime` as an opaque string), so an unparseable or empty value falls
 * back to the raw string verbatim rather than rendering "Invalid Date".
 *
 * Assembled from `formatToParts` rather than `format()` so the date and time are
 * joined by a single space, not the `", "` that `Intl` inserts between the two.
 *
 * Only strings that carry an explicit zone (`Z` or a numeric offset) are
 * re-expressed in Eastern time; a zone-less or date-only value is rendered
 * verbatim rather than being anchored to the viewer's local zone and shifted.
 * @param {string} raw - The raw timestamp string from the BFF (may be empty).
 * @returns {string} The EST-formatted timestamp, or the raw string if ambiguous/unparseable.
 */
export function formatNoteDatetime(raw: string): string {
  if (!raw) {
    return "";
  }
  if (!HAS_TIMEZONE.test(raw)) {
    return raw;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return raw;
  }
  const parts = new Map(EST_FORMAT.formatToParts(parsed).map((part) => [part.type, part.value]));
  return `${parts.get("month")}/${parts.get("day")}/${parts.get("year")} ${parts.get("hour")}:${parts.get("minute")} ${parts.get("dayPeriod")}`;
}
